import { API_ENDPOINTS } from '../config/apiConfig';
import { apiFetch } from '../config/apiClient';

/**
 * School seat licensing (#05).
 *
 * Every figure here comes from the server. Seat counts are the thing a school is
 * billed on, so the client never derives "available" by subtracting one number from
 * another it happens to hold — it reports what the licence says, and an assignment
 * that would exceed the pool is refused server-side with a reason worth showing.
 */

export interface SeatUsage {
  schoolId: string;
  hasLicence: boolean;
  plan: string | null;
  sku: string | null;
  seatsTotal: number;
  seatsUsed: number;
  seatsAvailable: number;
  utilisationPct: number | null;
  assignedStudentIds: string[];
  validTo: string | null;
}

const headers = (): HeadersInit => {
  const h: HeadersInit = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('token');
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
};

const base = (schoolId: string) => `${API_ENDPOINTS.learningApi}/schools/${schoolId}/seats`;

export type SeatResult =
  | { state: 'ready'; usage: SeatUsage }
  | { state: 'forbidden' | 'error'; message: string };

export const fetchSeatUsage = async (schoolId: string): Promise<SeatResult> => {
  try {
    const response = await apiFetch(base(schoolId), { headers: headers() });
    if (response.status === 403) {
      return { state: 'forbidden', message: 'Seat management is restricted to administrators.' };
    }
    if (!response.ok) return { state: 'error', message: `Could not load seats (${response.status}).` };
    const body = await response.json();
    return { state: 'ready', usage: body?.data ?? body };
  } catch {
    return { state: 'error', message: 'Could not reach the seat service.' };
  }
};

/** @returns null on success, or the server's reason for refusing. */
export const assignSeat = async (schoolId: string, studentId: string): Promise<string | null> => {
  try {
    const response = await apiFetch(
      `${base(schoolId)}/assign?studentId=${encodeURIComponent(studentId)}`,
      { method: 'POST', headers: headers() },
    );
    if (response.ok) return null;
    const body = await response.json().catch(() => ({}));
    // A 409 carries the actual reason — no licence, or the pool is exhausted. That
    // sentence is what an administrator needs; a generic failure is not actionable.
    return body?.message || `Could not assign a seat (${response.status}).`;
  } catch {
    return 'Could not reach the seat service.';
  }
};

export const releaseSeat = async (schoolId: string, studentId: string): Promise<string | null> => {
  try {
    const response = await apiFetch(
      `${base(schoolId)}/${encodeURIComponent(studentId)}`,
      { method: 'DELETE', headers: headers() },
    );
    if (response.ok) return null;
    const body = await response.json().catch(() => ({}));
    return body?.message || `Could not release the seat (${response.status}).`;
  } catch {
    return 'Could not reach the seat service.';
  }
};
